import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid, TextField, Typography, useTheme, CircularProgress, Box, Button,
  FormControlLabel, Autocomplete, Switch, Chip, alpha, MenuItem, Select,
  Collapse, InputAdornment, Divider, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Checkbox, Link,
} from "@mui/material";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { FormikProvider, useFormik } from "formik";
import { useNotifier } from "../../../Core/Notifier";
import {
  useCreateTeamMutation, useGetallEnumSearchQuery, useGetallLocationQuery,
  useGetDepartmentQuery, useGetPositionQuery, useGetUserRoleQuery,
  useEnumFindOrCreateMutation, useCheckStaffExistsMutation,
  useCreateDepartmentMutation, useCreatePositionMutation, useGetTeamQuery,
  useGetUserAdminOnyQuery,
} from "../../../Api/attoDeskApi";
import AddressAutocomplete, { ParsedAddress } from "../../../Components/AddressAutocomplete";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { CameraModal, CropModal, enhanceImage } from "../../../Components/ImageCapture";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  formatPhoneNumberForDisplay, formatPhoneNumber, validateEmail, validatePhoneNumber,
} from "../../../Components/Validation/validationUtils";
import {
  CustomerOption, CustomerOptionString, IEnumType, IStudentLocation, IDepartment, IPosition, ITeamEdit,
} from "../../../Api/Interface/api.interface";
import { useNavigate } from "react-router-dom";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import { Gender } from "../../../Core/Enum/enum";
import { skipToken } from "@reduxjs/toolkit/query";

// Icons
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RefreshIcon from "@mui/icons-material/Refresh";

const toAllCaps = (s: string | null | undefined): string => s ? s.toUpperCase() : "";

// ── Password generator ──
const generatePassword = (): string => {
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const special = "!@#$%&*";
  const all = upper + lower + digits + special;
  let pw = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    special[Math.floor(Math.random() * special.length)],
  ];
  for (let i = 0; i < 8; i++) pw.push(all[Math.floor(Math.random() * all.length)]);
  return pw.sort(() => Math.random() - 0.5).join("");
};

// ── Section Card ──
const SectionCard: React.FC<{
  icon: React.ReactNode; title: string; subtitle?: string; children: React.ReactNode;
  accentColor?: string; action?: React.ReactNode; collapsible?: boolean; defaultOpen?: boolean;
}> = ({ icon, title, subtitle, children, accentColor, action, collapsible, defaultOpen = true }) => {
  const theme = useTheme();
  const color = accentColor || theme.palette.primary.main;
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid rgba(0,0,0,0.08)", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
      <Box
        onClick={collapsible ? () => setOpen(!open) : undefined}
        sx={{
          display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 2,
          borderBottom: open ? "1px solid rgba(0,0,0,0.06)" : "none",
          borderLeft: `4px solid ${color}`,
          cursor: collapsible ? "pointer" : "default",
          userSelect: collapsible ? "none" : "auto",
        }}
      >
        <Box sx={{ color, display: "flex", alignItems: "center", "& svg": { fontSize: 20 } }}>{icon}</Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.1 }}>{subtitle}</Typography>}
        </Box>
        {action}
        {collapsible && (open ? <ExpandLessIcon sx={{ color: "text.disabled", fontSize: 20 }} /> : <ExpandMoreIcon sx={{ color: "text.disabled", fontSize: 20 }} />)}
      </Box>
      <Collapse in={open}>
        <Box sx={{ p: 3 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

// ── Field Label ──
const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({ label, required }) => (
  <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: "text.secondary", mb: 0.5 }}>
    {label}{required && <Typography component="span" sx={{ color: "error.main", ml: 0.25 }}>*</Typography>}
  </Typography>
);

// ── Quick-add Modal ──
const QuickAddModal: React.FC<{
  open: boolean; onClose: () => void; title: string;
  onSave: (name: string) => Promise<void>; loading?: boolean;
}> = ({ open, onClose, title, onSave, loading }) => {
  const [name, setName] = useState("");
  const handleSave = async () => { if (name.trim()) { await onSave(name.trim()); setName(""); } };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>{title}</DialogTitle>
      <DialogContent>
        <TextField autoFocus size="small" fullWidth placeholder="Name" value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
          sx={{ mt: 1 }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontSize: 13 }}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || loading} onClick={handleSave}
          sx={{ textTransform: "none", fontSize: 13, borderRadius: "10px", boxShadow: "none" }}>
          {loading ? <CircularProgress size={16} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Component ──
interface EditStaffProps {
  studentId?: string;
}

const Index = ({ studentId }: EditStaffProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();

  // Fetch existing staff data
  const { data: staffQueryData, isLoading: staffLoading } = useGetTeamQuery(
    studentId ? { searchText: "", id: String(studentId) } : skipToken
  );
  const studentData = useMemo(() => {
    if (staffQueryData && Array.isArray(staffQueryData.data) && staffQueryData.data.length > 0)
      return staffQueryData.data[0] as any;
    return null;
  }, [staffQueryData]);

  // Fetch portal user if exists
  const shouldFetchUser = !!studentData?.userPerentTableId;
  const { data: userPerenData } = useGetUserAdminOnyQuery(
    shouldFetchUser ? { searchText: "", id: String(studentData.userPerentTableId) } : skipToken
  );
  const existingPortalUser = useMemo(() => {
    if (userPerenData && Array.isArray(userPerenData.data) && userPerenData.data.length > 0)
      return userPerenData.data[0] as any;
    return null;
  }, [userPerenData]);

  // Image state
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isEnhanced, setIsEnhanced] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Portal access state
  const [portalEnabled, setPortalEnabled] = useState(false);
  const [portalEmail, setPortalEmail] = useState("");
  const [portalEmailManual, setPortalEmailManual] = useState(false);
  const [portalRoleId, setPortalRoleId] = useState<number | null>(null);
  const [portalError, setPortalError] = useState("");
  const [portalPassword, setPortalPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Duplicate check state
  const [emailExistsError, setEmailExistsError] = useState("");
  const [phoneExistsError, setPhoneExistsError] = useState("");

  // Multi-select location
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);

  // Quick-add modals
  const [deptModalOpen, setDeptModalOpen] = useState(false);
  const [posModalOpen, setPosModalOpen] = useState(false);

  // Hydrate state from fetched data
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (studentData && !hydrated) {
      // Portal
      if (existingPortalUser && existingPortalUser.status === "active") {
        setPortalEnabled(true);
        setPortalEmail(existingPortalUser.username || "");
        setPortalEmailManual(true);
        setPortalRoleId(existingPortalUser.user_role ? Number(existingPortalUser.user_role) : null);
      }
      // Location — handle both single id and JSON array
      const locId = studentData.location_id;
      if (locId) {
        try {
          const parsed = JSON.parse(locId);
          if (Array.isArray(parsed)) {
            setSelectedLocationIds(parsed.map(Number));
          } else {
            setSelectedLocationIds([Number(locId)]);
          }
        } catch {
          setSelectedLocationIds([Number(locId)]);
        }
      }
      setHydrated(true);
    }
  }, [studentData, existingPortalUser, hydrated]);

  // API hooks
  const [updateTeam, { isLoading }] = useCreateTeamMutation();
  const [enumFindOrCreate] = useEnumFindOrCreateMutation();
  const [checkStaffExists] = useCheckStaffExistsMutation();
  const [createDepartment, { isLoading: deptCreating }] = useCreateDepartmentMutation();
  const [createPosition, { isLoading: posCreating }] = useCreatePositionMutation();
  const { data: provinceData } = useGetallEnumSearchQuery({ searchText: "", enum_type: "province" });
  const { data: countryData } = useGetallEnumSearchQuery({ searchText: "", enum_type: "country" });
  const { data: cityData } = useGetallEnumSearchQuery({ searchText: "", enum_type: "city" });
  const { data: locationData, isLoading: locationIsLoading } = useGetallLocationQuery({ searchText: "" });
  const { data: departmentData, isLoading: departmentLoading, refetch: refetchDepartments } = useGetDepartmentQuery({ searchText: "" });
  const { data: positionData, isLoading: positionLoading, refetch: refetchPositions } = useGetPositionQuery({ searchText: "" });
  const { data: roleData } = useGetUserRoleQuery({ searchText: "" });

  // Memoized lists
  const provinceList = useMemo(() => (provinceData?.data ?? []) as IEnumType[], [provinceData]);
  const countryList = useMemo(() => (countryData?.data ?? []) as IEnumType[], [countryData]);
  const cityList = useMemo(() => (cityData?.data ?? []) as IEnumType[], [cityData]);
  const locationList = useMemo(() => (locationData?.data ?? []) as IStudentLocation[], [locationData]);
  const departmentList = useMemo(() => (departmentData?.data ?? []) as IDepartment[], [departmentData]);
  const positionList = useMemo(() => (positionData?.data ?? []) as IPosition[], [positionData]);
  const staffRoles = useMemo(() => {
    const roles = (roleData?.data ?? []) as any[];
    return roles.filter((r: any) => r.roleName !== "Parent" && !r.isDelete);
  }, [roleData]);

  // Autocomplete options
  const provinceOptions = useMemo(() => provinceList.map((c) => ({ label: c.enum_value, value: c.id })), [provinceList]);
  const countryOptions = useMemo(() => countryList.map((c) => ({ label: c.enum_value, value: c.id })), [countryList]);
  const cityOptions = useMemo(() => cityList.map((c) => ({ label: c.enum_value, value: c.id })), [cityList]);
  const locationOptions = useMemo(() => locationList.map((c) => ({ label: c.location_name || c.location_address, value: c.id })), [locationList]);
  const departmentOptions = useMemo(() => (departmentList || []).map((c) => ({ label: c.departmentName, value: c.id })), [departmentList]);
  const positionOptions = useMemo(() => (positionList || []).map((c) => ({ label: c.positionName, value: c.id })), [positionList]);
  const genderOptions: CustomerOptionString[] = Object.values(Gender).map((g) => ({ label: g, value: g }));

  // Phone formatter — caps at 10 digits so no more than a 10-digit number
  // can ever land in the input, and formats progressively as the user
  // types. See the same fix in Create staff for the "24 digits" regression.
  const handlePhoneChange = (field: string, value: string, setFieldValue: (f: string, v: any) => void) => {
    setFieldValue(field, formatPhoneNumber(value));
  };

  // ── Duplicate check handlers (exclude current staff) ──
  const handleEmailBlur = async (email: string) => {
    if (!email || validateEmail(email)) return;
    try {
      const res = await checkStaffExists({ email: email.trim().toLowerCase() }).unwrap();
      if (res.data && (res.data as any).email_exists) {
        // Check if it's the same staff member's own email
        if (studentData?.teamEmail?.toLowerCase() === email.trim().toLowerCase()) {
          setEmailExistsError("");
        } else {
          setEmailExistsError("Email already exists");
        }
      } else {
        setEmailExistsError("");
      }
    } catch {
      setEmailExistsError("");
    }
  };

  const handlePhoneBlur = async (phone: string) => {
    if (!phone || validatePhoneNumber(phone)) return;
    try {
      const cleanPhone = phone.replace(/-/g, "");
      const res = await checkStaffExists({ phone: cleanPhone }).unwrap();
      if (res.data && (res.data as any).phone_exists) {
        // Check if it's the same staff member's own phone
        if (studentData?.teamMobileNum === cleanPhone) {
          setPhoneExistsError("");
        } else {
          setPhoneExistsError("Phone number already exists");
        }
      } else {
        setPhoneExistsError("");
      }
    } catch {
      setPhoneExistsError("");
    }
  };

  // ── Quick-add handlers ──
  const handleAddDepartment = async (name: string) => {
    try {
      const res = await createDepartment({ departmentName: name }).unwrap();
      if (res.status && res.data) {
        refetchDepartments();
        formik.setFieldValue("teamDepartmentId", (res.data as any).id);
        showMessage("Department created");
      } else {
        showErrorMessage(res.message || "Failed to create department");
      }
    } catch { showErrorMessage("Failed to create department"); }
    setDeptModalOpen(false);
  };

  const handleAddPosition = async (name: string) => {
    try {
      const res = await createPosition({ positionName: name }).unwrap();
      if (res.status && res.data) {
        refetchPositions();
        formik.setFieldValue("teamPositionId", (res.data as any).id);
        showMessage("Position created");
      } else {
        showErrorMessage(res.message || "Failed to create position");
      }
    } catch { showErrorMessage("Failed to create position"); }
    setPosModalOpen(false);
  };

  // Auto-enhance helper
  const applyEnhancement = async (file: File) => {
    setIsEnhancing(true);
    setIsEnhanced(false);
    try {
      const enhanced = await enhanceImage(file);
      setSelectedImage(enhanced);
      setIsEnhanced(true);
    } catch {
      setSelectedImage(file);
    } finally {
      setIsEnhancing(false);
    }
  };

  // Image handlers
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.match(/^image\/(jpeg|png)$/)) { showErrorMessage("Only JPEG and PNG images are accepted"); return; }
    if (file.size > 5 * 1024 * 1024) { showErrorMessage("File must be under 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setCropImageSrc(reader.result as string); setCropOpen(true); };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const handleCameraCapture = (dataUrl: string) => { setCropImageSrc(dataUrl); setCropOpen(true); };
  const handleCropComplete = (croppedFile: File) => applyEnhancement(croppedFile);

  // Formik
  const formik = useFormik<ITeamEdit>({
    initialValues: {
      id: String(studentData?.id || ""),
      teamMobileNum: studentData?.teamMobileNum ? formatPhoneNumberForDisplay(studentData.teamMobileNum) : "",
      teamId: studentData?.teamId || "",
      teamPositionId: studentData?.teamPositionId || null,
      teamHourlySalary: studentData?.teamHourlySalary || "",
      teamOtHourlySalary: studentData?.teamOtHourlySalary || "",
      teamDepartmentId: studentData?.teamDepartmentId || null,
      teamFirstName: studentData?.teamFirstName || "",
      teamLastName: studentData?.teamLastName || "",
      teamAltMobileNum: studentData?.teamAltMobileNum ? formatPhoneNumberForDisplay(studentData.teamAltMobileNum) : "",
      teamImg: studentData?.teamImg || "",
      teamCity: studentData?.teamCity || "",
      teamAddress: studentData?.teamAddress || "",
      teamEmail: studentData?.teamEmail || "",
      teamDescription: studentData?.teamDescription || "",
      teamUserName: studentData?.teamUserName || "",
      teamPassWord: studentData?.teamPassWord || "",
      teamStreet: studentData?.teamStreet || "",
      teamProvince: studentData?.teamProvince || "",
      teamPostalCode: studentData?.teamPostalCode || "",
      emailReminder: Boolean(studentData?.emailReminder),
      smsReminder: Boolean(studentData?.smsReminder),
      availability: studentData?.availability?.map((a: any) => ({
        day: a.day,
        timeSlots: a.timeSlots?.map((s: any) => ({
          id: s.id, startTime: s.startTime || "", endTime: s.endTime || "",
          hourlySalary: s.hourlySalary || "", spansNextDay: s.spansNextDay || false,
        })) || [],
      })) || [
        { day: "Sunday", timeSlots: [] }, { day: "Monday", timeSlots: [] },
        { day: "Tuesday", timeSlots: [] }, { day: "Wednesday", timeSlots: [] },
        { day: "Thursday", timeSlots: [] }, { day: "Friday", timeSlots: [] },
        { day: "Saturday", timeSlots: [] },
      ],
      middle_name: studentData?.middle_name || "",
      location_id: studentData?.location_id || "",
      gender: studentData?.gender || "",
      mode: studentData?.mode || "",
      unit_number: studentData?.address?.unit_number || "",
      street: studentData?.address?.street || "",
      route: studentData?.address?.route || "",
      city_id: studentData?.address?.city_id || null,
      province_id: studentData?.address?.province_id || null,
      postal_code: studentData?.address?.postal_code || "",
      country_id: studentData?.address?.country_id || null,
      address_id: studentData?.address_id || null,
      is_class_reminder: Boolean(studentData?.is_class_reminder),
      email_credential_reminder: Boolean(studentData?.email_credential_reminder),
      sms_credential_reminder: Boolean(studentData?.sms_credential_reminder),
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      if (!values.teamFirstName?.trim()) errors.teamFirstName = "First name is required";
      if (!values.teamLastName?.trim()) errors.teamLastName = "Last name is required";
      if (values.teamEmail) { const e = validateEmail(values.teamEmail); if (e) errors.teamEmail = e; }
      if (values.teamMobileNum) { const e = validatePhoneNumber(values.teamMobileNum); if (e) errors.teamMobileNum = e; }
      if (values.teamAltMobileNum) { const e = validatePhoneNumber(values.teamAltMobileNum); if (e) errors.teamAltMobileNum = e; }
      return errors;
    },
    onSubmit: async (values, { setFieldError, setFieldTouched }) => {
      if (portalEnabled) {
        const email = portalEmail || values.teamEmail?.trim().toLowerCase();
        if (!email) { setPortalError("Email is required for portal access."); showErrorMessage("Email is required for portal access."); return; }
        if (!portalRoleId) { setPortalError("Please select a role for portal access."); showErrorMessage("Please select a role."); return; }
        setPortalError("");
      }
      try {
        let teamImg: string | undefined = values.teamImg;
        if (selectedImage) { setIsImageUploading(true); teamImg = (await uploadImage(selectedImage)) || ""; setIsImageUploading(false); }
        const payload = {
          id: Number(values.id),
          teamMobileNum: values.teamMobileNum.replace(/-/g, ""),
          teamAltMobileNum: values.teamAltMobileNum.replace(/-/g, ""),
          teamId: values.teamId, teamPositionId: values.teamPositionId,
          teamHourlySalary: String(values.teamHourlySalary || ""), teamOtHourlySalary: String(values.teamOtHourlySalary || ""),
          teamDepartmentId: values.teamDepartmentId,
          teamFirstName: toProperCase(values.teamFirstName), teamLastName: toProperCase(values.teamLastName),
          teamCity: toProperCase(values.teamCity), teamAddress: values.teamAddress,
          teamEmail: values.teamEmail?.trim().toLowerCase(), teamDescription: values.teamDescription,
          teamUserName: values.teamUserName, teamPassWord: values.teamPassWord,
          teamStreet: toProperCase(values.teamStreet), teamProvince: toProperCase(values.teamProvince),
          teamPostalCode: toAllCaps(values.teamPostalCode), teamImg,
          availability: values.availability, emailReminder: values.emailReminder, smsReminder: values.smsReminder,
          middle_name: toProperCase(values.middle_name), gender: values.gender, mode: values.mode,
          location_ids: selectedLocationIds,
          unit_number: values.unit_number, street: values.street,
          route: values.route,
          city_id: values.city_id ? Number(values.city_id) : null,
          province_id: values.province_id ? Number(values.province_id) : null,
          postal_code: toAllCaps(values.postal_code),
          country_id: values.country_id ? Number(values.country_id) : null,
          address_id: values.address_id ? Number(values.address_id) : null,
          sms_credential_reminder: values.sms_credential_reminder,
          email_credential_reminder: values.email_credential_reminder,
          is_class_reminder: values.is_class_reminder,
          notifications: { class_reminders: true },
          portal_access_enabled: portalEnabled,
          portal_email: portalEnabled ? (portalEmail || values.teamEmail?.trim().toLowerCase()) : undefined,
          portal_role_id: portalEnabled ? portalRoleId : undefined,
          password: portalEnabled && portalPassword ? portalPassword : undefined,
        };
        const response = await updateTeam(payload).unwrap();
        if (!response.status) {
          // Surface field-level errors (e.g. duplicate teamEmail) inline
          // on the form instead of only as a top toast — same shape the
          // backend now emits for every save path in TeamController.
          const errs = (response as any)?.errors;
          let fielded = false;
          if (errs && typeof errs === "object") {
            Object.entries(errs).forEach(([field, msgs]) => {
              const first = Array.isArray(msgs) ? msgs[0] : msgs;
              if (typeof first === "string" && first.trim() !== "") {
                setFieldError(field, first);
                setFieldTouched(field, true, false);
                fielded = true;
              }
            });
          }
          if (!fielded) showErrorMessage(response.message);
        }
        else {
          showMessage("Staff updated successfully");
          setSelectedImage(undefined);
          setIsEnhanced(false);
          // Explicit route (not navigate(-1)) so the flow also works
          // when Edit was opened directly / after a refresh — user
          // always lands back on this staff member's profile. The
          // mutation invalidates the `team` tag, so the profile's
          // queries refetch and show the just-saved values.
          if (studentId) navigate(`/team-history/${studentId}`);
        }
      } catch (err: any) {
        const errs = err?.data?.errors;
        if (errs && typeof errs === "object") {
          Object.entries(errs).forEach(([field, msgs]) => {
            const first = Array.isArray(msgs) ? msgs[0] : msgs;
            if (typeof first === "string" && first.trim() !== "") {
              setFieldError(field, first);
              setFieldTouched(field, true, false);
            }
          });
        }
        const msg = err?.data?.message || err?.message || "Something went wrong";
        showErrorMessage(msg);
      }
    },
  });

  const formValid = useMemo(() => {
    return !!(
      formik.values.teamFirstName?.trim() &&
      formik.values.teamLastName?.trim() &&
      !emailExistsError &&
      !phoneExistsError
    );
  }, [formik.values.teamFirstName, formik.values.teamLastName, emailExistsError, phoneExistsError]);

  // Auto-sync portal email
  useEffect(() => {
    if (portalEnabled && !portalEmailManual && formik.values.teamEmail) {
      setPortalEmail(formik.values.teamEmail.trim().toLowerCase());
    }
  }, [formik.values.teamEmail, portalEnabled, portalEmailManual]);

  // Address auto-fill
  const handleAddressAutoFill = useCallback(
    async (addr: ParsedAddress) => {
      if (addr.street) formik.setFieldValue("street", addr.street);
      if (addr.postalCode) formik.setFieldValue("postal_code", addr.postalCode.toUpperCase());

      const findMatch = (options: CustomerOption[], candidates: string[]) => {
        for (const candidate of candidates) {
          if (!candidate) continue;
          const lower = candidate.toLowerCase().trim();
          for (const opt of options) {
            const optLower = opt.label.toLowerCase().trim();
            if (optLower === lower || optLower.includes(lower) || lower.includes(optLower)) return opt;
          }
        }
        return null;
      };

      const cityCandidates = [addr.suburb, addr.city].filter(Boolean);
      const cityMatch = findMatch(cityOptions, cityCandidates);
      if (cityMatch) {
        formik.setFieldValue("city_id", String(cityMatch.value));
      } else {
        const cityName = addr.suburb || addr.city;
        if (cityName) {
          try {
            const r = await enumFindOrCreate({ enum_type: "city", enum_value: cityName }).unwrap();
            if (r.status && r.data) formik.setFieldValue("city_id", String((r.data as any).id));
          } catch {}
        }
      }

      if (addr.province) {
        const provMatch = findMatch(provinceOptions, [addr.province]);
        if (provMatch) {
          formik.setFieldValue("province_id", String(provMatch.value));
        } else {
          try {
            const r = await enumFindOrCreate({ enum_type: "province", enum_value: addr.province }).unwrap();
            if (r.status && r.data) formik.setFieldValue("province_id", String((r.data as any).id));
          } catch {}
        }
      }

      if (addr.country) {
        const countryMatch = findMatch(countryOptions, [addr.country]);
        if (countryMatch) {
          formik.setFieldValue("country_id", String(countryMatch.value));
        } else {
          try {
            const r = await enumFindOrCreate({ enum_type: "country", enum_value: addr.country }).unwrap();
            if (r.status && r.data) formik.setFieldValue("country_id", String((r.data as any).id));
          } catch {}
        }
      }
    },
    [cityOptions, provinceOptions, countryOptions, formik, enumFindOrCreate]
  );

  // Existing image URL for preview
  const existingImageUrl = studentData?.teamImg || "";

  if (staffLoading || locationIsLoading || departmentLoading || positionLoading) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="30vh"><StepUpSpinner size="medium" /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            {/* ═══ SECTION 1: Profile Setup ═══ */}
            <SectionCard icon={<PersonOutlineIcon />} title="Profile Setup" subtitle="Name, gender, and photo">
              <Grid container spacing={3}>
                {/* Photo */}
                <Grid item lg={3} md={4} sm={12} xs={12}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      position: "relative", width: 140, height: 140, borderRadius: "50%",
                      border: (selectedImage || existingImageUrl) ? `3px solid ${theme.palette.primary.main}` : "2px dashed rgba(0,0,0,0.12)",
                      display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", bgcolor: "rgba(0,0,0,0.02)",
                    }}>
                      {isEnhancing ? (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                          <CircularProgress size={28} />
                          <Typography sx={{ fontSize: 9.5, color: "text.disabled" }}>Enhancing...</Typography>
                        </Box>
                      ) : selectedImage ? (
                        <>
                          <img src={URL.createObjectURL(selectedImage)} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          <Box onClick={() => { setCropImageSrc(URL.createObjectURL(selectedImage)); setCropOpen(true); }}
                            sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s", cursor: "pointer", "&:hover": { opacity: 1 } }}>
                            <EditOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />
                          </Box>
                        </>
                      ) : existingImageUrl ? (
                        <>
                          <img src={existingImageUrl} alt="Current" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          <Box onClick={() => { setCropImageSrc(existingImageUrl); setCropOpen(true); }}
                            sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s", cursor: "pointer", "&:hover": { opacity: 1 } }}>
                            <EditOutlinedIcon sx={{ color: "#fff", fontSize: 22 }} />
                          </Box>
                        </>
                      ) : (
                        <Box sx={{ textAlign: "center" }}>
                          <CloudUploadIcon sx={{ fontSize: 28, color: "rgba(0,0,0,0.18)" }} />
                          <Typography sx={{ fontSize: 10.5, color: "text.disabled" }}>No Photo</Typography>
                        </Box>
                      )}
                    </Box>
                    {isEnhanced && selectedImage && (
                      <Chip label="Auto Enhanced" size="small"
                        sx={{ fontSize: 10, height: 20, bgcolor: alpha("#22c55e", 0.1), color: "#16a34a", fontWeight: 600 }} />
                    )}
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button size="small" variant="outlined" startIcon={<CameraAltIcon sx={{ fontSize: "14px !important" }} />}
                        onClick={() => setCameraOpen(true)} disabled={isEnhancing}
                        sx={{ borderRadius: "8px", textTransform: "none", fontSize: 11.5, py: 0.4, borderColor: "rgba(0,0,0,0.12)", color: "text.secondary" }}>
                        Camera
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<CloudUploadIcon sx={{ fontSize: "14px !important" }} />}
                        onClick={() => fileInputRef.current?.click()} disabled={isEnhancing}
                        sx={{ borderRadius: "8px", textTransform: "none", fontSize: 11.5, py: 0.4, borderColor: "rgba(0,0,0,0.12)", color: "text.secondary" }}>
                        Upload
                      </Button>
                    </Box>
                    {(selectedImage || existingImageUrl) && !isEnhancing && (
                      <Button size="small" onClick={() => { setSelectedImage(undefined); setIsEnhanced(false); formik.setFieldValue("teamImg", ""); }}
                        sx={{ fontSize: 11, textTransform: "none", color: "text.disabled" }}>Remove</Button>
                    )}
                    <Typography sx={{ fontSize: 10, color: "text.disabled", textAlign: "center" }}>
                      Use good lighting for best results
                    </Typography>
                    <input ref={fileInputRef} accept="image/jpeg,image/png" type="file" onChange={handleImageChange} style={{ display: "none" }} />
                  </Box>
                </Grid>
                {/* Name Fields */}
                <Grid item lg={9} md={8} sm={12} xs={12}>
                  <Grid container spacing={2}>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="First Name" required />
                      <TextField size="small" fullWidth placeholder="First name" {...formik.getFieldProps("teamFirstName")}
                        error={!!formik.touched.teamFirstName && !!formik.errors.teamFirstName}
                        helperText={formik.touched.teamFirstName && formik.errors.teamFirstName} />
                    </Grid>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="Middle Name" />
                      <TextField size="small" fullWidth placeholder="Middle name" {...formik.getFieldProps("middle_name")} />
                    </Grid>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="Last Name" required />
                      <TextField size="small" fullWidth placeholder="Last name" {...formik.getFieldProps("teamLastName")}
                        error={!!formik.touched.teamLastName && !!formik.errors.teamLastName}
                        helperText={formik.touched.teamLastName && formik.errors.teamLastName} />
                    </Grid>
                    <Grid item lg={4} md={4} sm={6} xs={12}>
                      <FieldLabel label="Gender" />
                      <Autocomplete
                        options={genderOptions}
                        getOptionLabel={(o: CustomerOptionString) => o.label}
                        value={genderOptions.find((o) => o.value === formik.values.gender) || null}
                        onChange={(_, v) => formik.setFieldValue("gender", v?.value || "")}
                        renderInput={(params) => <TextField {...params} placeholder="Select" size="small" />}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </SectionCard>

            {/* ═══ SECTION 2: Contact Info ═══ */}
            <SectionCard icon={<PhoneIcon />} title="Contact Information" subtitle="Phone numbers and email" accentColor="#3b82f6">
              <Grid container spacing={2}>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Main Phone" />
                  <TextField size="small" fullWidth placeholder="000-000-0000"
                    value={formik.values.teamMobileNum}
                    onChange={(e) => { handlePhoneChange("teamMobileNum", e.target.value, formik.setFieldValue); setPhoneExistsError(""); }}
                    onBlur={() => handlePhoneBlur(formik.values.teamMobileNum)}
                    error={!!formik.errors.teamMobileNum || !!phoneExistsError}
                    helperText={formik.errors.teamMobileNum || phoneExistsError}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 16, color: "text.disabled" }} /></InputAdornment> }}
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Emergency Phone" />
                  <TextField size="small" fullWidth placeholder="000-000-0000"
                    value={formik.values.teamAltMobileNum}
                    onChange={(e) => handlePhoneChange("teamAltMobileNum", e.target.value, formik.setFieldValue)}
                    error={!!formik.errors.teamAltMobileNum} helperText={formik.errors.teamAltMobileNum}
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Email" />
                  <TextField size="small" fullWidth placeholder="email@example.com" type="email"
                    {...formik.getFieldProps("teamEmail")}
                    onChange={(e) => { formik.setFieldValue("teamEmail", e.target.value); setEmailExistsError(""); }}
                    onBlur={() => handleEmailBlur(formik.values.teamEmail)}
                    error={!!formik.errors.teamEmail || !!emailExistsError}
                    helperText={formik.errors.teamEmail || emailExistsError}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ═══ SECTION 3: Address (collapsible) ═══ */}
            <SectionCard icon={<HomeOutlinedIcon />} title="Address" subtitle="Staff member's home address"
              accentColor="#8b5cf6" collapsible defaultOpen={!!(studentData?.address?.street)}>
              <Box sx={{ mb: 2.5 }}>
                <AddressAutocomplete
                  placeholder="Start typing an address to auto-fill..."
                  countryCode="ca"
                  onAddressSelect={(addr: ParsedAddress) => handleAddressAutoFill(addr)}
                />
                <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.5 }}>
                  Select a result to auto-fill the fields below
                </Typography>
              </Box>
              <Divider sx={{ mb: 2.5 }} />
              <Grid container spacing={2}>
                <Grid item lg={2} md={3} sm={6} xs={12}>
                  <FieldLabel label="Unit #" />
                  <TextField size="small" fullWidth placeholder="Apt/Unit" {...formik.getFieldProps("unit_number")} />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Street" />
                  <TextField size="small" fullWidth placeholder="Street address" {...formik.getFieldProps("street")} />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <FieldLabel label="City" />
                  <Autocomplete
                    options={cityOptions} getOptionLabel={(o: any) => o.label}
                    value={cityOptions.find((o) => o.value === Number(formik.values.city_id)) || null}
                    onChange={(_, v) => formik.setFieldValue("city_id", v?.value || "")}
                    renderInput={(params) => <TextField {...params} placeholder="Select" size="small" />}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <FieldLabel label="Province" />
                  <Autocomplete
                    options={provinceOptions} getOptionLabel={(o: any) => o.label}
                    value={provinceOptions.find((o) => o.value === Number(formik.values.province_id)) || null}
                    onChange={(_, v) => formik.setFieldValue("province_id", v?.value || "")}
                    renderInput={(params) => <TextField {...params} placeholder="Select" size="small" />}
                  />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <FieldLabel label="Postal Code" />
                  <TextField size="small" fullWidth placeholder="A1A 1A1" {...formik.getFieldProps("postal_code")} />
                </Grid>
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <FieldLabel label="Country" />
                  <Autocomplete
                    options={countryOptions} getOptionLabel={(o: any) => o.label}
                    value={countryOptions.find((o) => o.value === Number(formik.values.country_id)) || null}
                    onChange={(_, v) => formik.setFieldValue("country_id", v?.value || "")}
                    renderInput={(params) => <TextField {...params} placeholder="Select" size="small" />}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ═══ SECTION 4: Work Settings ═══ */}
            <SectionCard icon={<WorkOutlineIcon />} title="Work Settings" subtitle="Mode, location, and notification preferences" accentColor="#f59e0b">
              <Grid container spacing={2.5}>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Teaching Mode" />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {[{ label: "In Person", value: "InPerson" }, { label: "Online", value: "Online" }].map((opt) => {
                      const modes = (formik.values.mode || "").split(",").filter(Boolean);
                      const active = modes.includes(opt.value);
                      const toggle = () => {
                        const updated = active ? modes.filter((m) => m !== opt.value) : [...modes, opt.value];
                        formik.setFieldValue("mode", updated.join(","));
                      };
                      return (
                        <Box key={opt.value} onClick={toggle}
                          sx={{
                            flex: 1, py: 1, textAlign: "center", borderRadius: "10px", cursor: "pointer",
                            border: `1.5px solid ${active ? theme.palette.primary.main : "rgba(0,0,0,0.1)"}`,
                            bgcolor: active ? alpha(theme.palette.primary.main, 0.06) : "transparent",
                            color: active ? theme.palette.primary.main : "text.secondary",
                            fontSize: 12.5, fontWeight: active ? 600 : 500, transition: "all 0.15s",
                          }}>
                          {opt.label}
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <FieldLabel label="Location" />
                    {locationOptions.length > 0 && (
                      <Link component="button" type="button" underline="hover" sx={{ fontSize: 12 }}
                        onClick={() =>
                          setSelectedLocationIds(
                            selectedLocationIds.length === locationOptions.length
                              ? []
                              : locationOptions.map((o) => o.value)
                          )
                        }>
                        {selectedLocationIds.length === locationOptions.length ? "Clear All" : "Select All"}
                      </Link>
                    )}
                  </Box>
                  <Autocomplete
                    multiple
                    options={locationOptions}
                    getOptionLabel={(o: any) => o.label}
                    value={locationOptions.filter((o) => selectedLocationIds.includes(o.value))}
                    onChange={(_, v) => setSelectedLocationIds(v.map((item: any) => item.value))}
                    disableCloseOnSelect
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                        {option.label}
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option: any, index: number) => (
                        <Chip {...getTagProps({ index })} key={option.value} label={option.label} size="small"
                          sx={{ borderRadius: "8px", fontSize: 11.5, height: 24 }} />
                      ))
                    }
                    renderInput={(params) => <TextField {...params} placeholder={selectedLocationIds.length ? "" : "Select locations"} size="small" />}
                  />
                </Grid>
                <Grid item lg={4} md={4} sm={12} xs={12}>
                  <FieldLabel label="Notifications" />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <FormControlLabel sx={{ m: 0, "& .MuiTypography-root": { fontSize: 12.5 } }}
                      control={<Switch size="small" checked={formik.values.emailReminder} onChange={(e) => formik.setFieldValue("emailReminder", e.target.checked)} />}
                      label="Email reminders" />
                    <FormControlLabel sx={{ m: 0, "& .MuiTypography-root": { fontSize: 12.5 } }}
                      control={<Switch size="small" checked={formik.values.smsReminder} onChange={(e) => formik.setFieldValue("smsReminder", e.target.checked)} />}
                      label="SMS reminders" />
                    <FormControlLabel sx={{ m: 0, "& .MuiTypography-root": { fontSize: 12.5 } }}
                      control={<Switch size="small" checked={formik.values.is_class_reminder} onChange={(e) => formik.setFieldValue("is_class_reminder", e.target.checked)} />}
                      label="Class reminders" />
                  </Box>
                </Grid>
              </Grid>
            </SectionCard>

            {/* ═══ SECTION 5: Employment Details ═══ */}
            <SectionCard icon={<BadgeOutlinedIcon />} title="Employment Details" subtitle="ID, department, position, and salary" accentColor="#22c55e">
              <Grid container spacing={2}>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Staff ID" />
                  <TextField size="small" fullWidth value={formik.values.teamId}
                    onChange={(e) => formik.setFieldValue("teamId", e.target.value)} />
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Department" />
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Autocomplete
                      sx={{ flex: 1 }}
                      options={departmentOptions} getOptionLabel={(o: any) => o.label}
                      value={departmentOptions.find((o) => o.value === Number(formik.values.teamDepartmentId)) || null}
                      onChange={(_, v) => formik.setFieldValue("teamDepartmentId", v?.value || "")}
                      renderInput={(params) => <TextField {...params} placeholder="Select" size="small" />}
                    />
                    <IconButton size="small" onClick={() => setDeptModalOpen(true)}
                      sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: "8px", width: 36, height: 36 }}>
                      <AddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Grid>
                <Grid item lg={4} md={4} sm={6} xs={12}>
                  <FieldLabel label="Position" />
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Autocomplete
                      sx={{ flex: 1 }}
                      options={positionOptions} getOptionLabel={(o: any) => o.label}
                      value={positionOptions.find((o) => o.value === Number(formik.values.teamPositionId)) || null}
                      onChange={(_, v) => formik.setFieldValue("teamPositionId", v?.value || "")}
                      renderInput={(params) => <TextField {...params} placeholder="Select" size="small" />}
                    />
                    <IconButton size="small" onClick={() => setPosModalOpen(true)}
                      sx={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: "8px", width: 36, height: 36 }}>
                      <AddIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Grid>
                <Grid item lg={3} md={4} sm={6} xs={12}>
                  <FieldLabel label="Hourly Salary ($)" />
                  <TextField size="small" fullWidth type="number" placeholder="0.00" {...formik.getFieldProps("teamHourlySalary")}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                </Grid>
                <Grid item lg={3} md={4} sm={6} xs={12}>
                  <FieldLabel label="OT Hourly Salary ($)" />
                  <TextField size="small" fullWidth type="number" placeholder="0.00" {...formik.getFieldProps("teamOtHourlySalary")}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ═══ SECTION 6: Notes ═══ */}
            <SectionCard icon={<NotesOutlinedIcon />} title="Notes" subtitle="Internal notes about this staff member"
              accentColor="#6b7280" collapsible defaultOpen={!!studentData?.teamDescription}>
              <TextField size="small" fullWidth multiline rows={3} placeholder="Add any notes about this staff member..."
                {...formik.getFieldProps("teamDescription")} />
            </SectionCard>

            {/* ═══ SECTION 7: Portal Access ═══ */}
            <SectionCard icon={<VpnKeyIcon />} title="Portal Access" subtitle="Allow this staff member to log into the platform"
              accentColor="#6366f1"
              action={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} onClick={(e) => e.stopPropagation()}>
                  <Switch size="small" checked={portalEnabled} onChange={(e) => setPortalEnabled(e.target.checked)} />
                  <Chip label={portalEnabled ? "Enabled" : "Disabled"} size="small"
                    sx={{ fontWeight: 600, fontSize: 10.5, height: 22,
                      bgcolor: portalEnabled ? alpha("#22c55e", 0.1) : alpha("#ef4444", 0.08),
                      color: portalEnabled ? "#16a34a" : "#dc2626",
                    }} />
                </Box>
              }>
              {portalEnabled ? (
                <Grid container spacing={2}>
                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <FieldLabel label="Login Email" required />
                    <TextField size="small" fullWidth placeholder="Auto-filled from staff email"
                      value={portalEmail || formik.values.teamEmail || ""}
                      onChange={(e) => { setPortalEmail(e.target.value); setPortalEmailManual(true); setPortalError(""); }}
                      error={!!portalError && !portalEmail && !formik.values.teamEmail} />
                    <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.5 }}>This will be the username for login</Typography>
                  </Grid>
                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <FieldLabel label="Role" required />
                    <Select size="small" fullWidth value={portalRoleId || ""} displayEmpty
                      onChange={(e) => { setPortalRoleId(Number(e.target.value)); setPortalError(""); }}
                      error={!!portalError && !portalRoleId}>
                      <MenuItem value="" disabled>Select a role</MenuItem>
                      {staffRoles.map((r: any) => <MenuItem key={r.id} value={r.id}>{r.roleName}</MenuItem>)}
                    </Select>
                    <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.5 }}>Controls what this staff member can access</Typography>
                  </Grid>
                  <Grid item lg={4} md={4} sm={12} xs={12}>
                    <FieldLabel label="Password" />
                    <TextField size="small" fullWidth
                      type={showPassword ? "text" : "password"}
                      value={portalPassword}
                      onChange={(e) => setPortalPassword(e.target.value)}
                      placeholder={existingPortalUser ? "Leave blank to keep current" : "Auto-generated"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                              {showPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                            <IconButton size="small" onClick={() => setPortalPassword(generatePassword())} edge="end" title="Generate new password">
                              <RefreshIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography sx={{ fontSize: 10.5, color: "text.disabled", mt: 0.5 }}>
                      {existingPortalUser ? "Leave blank to keep current password" : "Click refresh to generate a password"}
                    </Typography>
                  </Grid>
                  {portalError && (
                    <Grid item xs={12}>
                      <Typography sx={{ fontSize: 12, color: "error.main", fontWeight: 600 }}>{portalError}</Typography>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography sx={{ fontSize: 12.5, color: "text.disabled" }}>
                  Enable the toggle above to configure login credentials for this staff member.
                </Typography>
              )}
            </SectionCard>
          </Box>

          {/* ── Sticky Footer ── */}
          <Box sx={{
            position: "sticky", bottom: 0, mt: 3, py: 2, px: 3,
            bgcolor: "#fff", borderRadius: "12px", border: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
            display: "flex", justifyContent: "flex-end", gap: 1.5,
          }}>
            <Button variant="outlined" onClick={() => navigate(`/team-history/${studentId}`)}
              sx={{ borderRadius: "10px", textTransform: "none", fontSize: 13, borderColor: "rgba(0,0,0,0.12)", color: "text.secondary", px: 3 }}>
              Cancel
            </Button>
            <Button variant="contained" startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
              onClick={() => formik.handleSubmit()} disabled={!formValid || isLoading}
              sx={{ borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600, boxShadow: "none", px: 3 }}>
              Save Changes
            </Button>
          </Box>
        </form>
      </FormikProvider>

      {/* ── Modals ── */}
      <CameraModal open={cameraOpen} onClose={() => setCameraOpen(false)} onCapture={handleCameraCapture} />
      <CropModal open={cropOpen} imageSrc={cropImageSrc} onClose={() => { setCropOpen(false); setCropImageSrc(null); }} onCropComplete={handleCropComplete} />
      <QuickAddModal open={deptModalOpen} onClose={() => setDeptModalOpen(false)} title="Add New Department" onSave={handleAddDepartment} loading={deptCreating} />
      <QuickAddModal open={posModalOpen} onClose={() => setPosModalOpen(false)} title="Add New Position" onSave={handleAddPosition} loading={posCreating} />
    </Box>
  );
};

export default Index;
